/**
 * Sessão do web: tokens do Phoenix A (via proxy da API) + casa escolhida.
 * Access token expirado é renovado de forma transparente pelo refresh token
 * (rotação: cada refresh invalida o anterior); só cai para /login quando o
 * próprio refresh é recusado.
 */

const STORAGE_KEY = 'zeladoria.session';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt?: string;
  refreshExpiresAt?: string;
  email: string;
  houseId?: string;
  houseName?: string;
}

export interface SessionHouse {
  id: string;
  name: string;
}

export function getSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.accessToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function setSessionHouse(house: SessionHouse): void {
  const current = getSession();
  if (!current) return;
  saveSession({ ...current, houseId: house.id, houseName: house.name });
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

interface LoginResponse {
  data: {
    accessToken: string;
    refreshToken: string;
    accessExpiresAt?: string;
    refreshExpiresAt?: string;
  };
}

interface HousesResponse {
  data: SessionHouse[];
  meta?: { onboarding?: { canCreateHouse?: boolean } };
}

export interface SessionHouseAccess {
  houses: SessionHouse[];
  onboarding: { canCreateHouse: boolean };
}

async function parseError(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { errors?: { message?: string }[] };
  return body.errors?.[0]?.message ?? fallback;
}

export type EmailAccessStatus = 'unknown' | 'invited' | 'active';

export async function lookupEmailStatus(email: string): Promise<EmailAccessStatus> {
  const res = await fetch('/api/v1/session/email-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(await parseError(res, 'Não foi possível consultar o e-mail.'));
  }
  const body = (await res.json()) as { data?: { status?: string } };
  const status = body.data?.status;
  if (status === 'invited' || status === 'active' || status === 'unknown') return status;
  return 'unknown';
}

export async function activateAccount(
  email: string,
  activationSecret: string,
  password: string,
): Promise<StoredSession> {
  const res = await fetch('/api/v1/session/activate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, activationSecret, password }),
  });
  if (!res.ok) {
    throw new Error(await parseError(res, 'Não foi possível ativar o acesso.'));
  }
  const body = (await res.json()) as LoginResponse;
  const session: StoredSession = { ...body.data, email };
  saveSession(session);
  return session;
}

export async function login(email: string, password: string): Promise<StoredSession> {
  const res = await fetch('/api/v1/session/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new Error(await parseError(res, 'Não foi possível entrar. Tente novamente.'));
  }
  const body = (await res.json()) as LoginResponse;
  const session: StoredSession = { ...body.data, email };
  saveSession(session);
  return session;
}

export async function fetchHouses(session: StoredSession): Promise<SessionHouse[]> {
  return (await fetchHouseAccess(session)).houses;
}

export async function fetchHouseAccess(session: StoredSession): Promise<SessionHouseAccess> {
  const res = await fetch('/api/v1/session/houses', {
    headers: { Authorization: `Bearer ${session.accessToken}` },
  });
  if (!res.ok) {
    throw new Error(await parseError(res, 'Não foi possível listar as casas do seu acesso.'));
  }
  const body = (await res.json()) as HousesResponse;
  return {
    houses: body.data,
    onboarding: {
      canCreateHouse: body.meta?.onboarding?.canCreateHouse === true,
    },
  };
}

export async function createFirstHouse(
  session: StoredSession,
  input: { displayName: string; houseName: string },
): Promise<unknown> {
  const res = await fetch('/api/v1/session/first-house', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ displayName: input.displayName, houseName: input.houseName }),
  });
  if (!res.ok)
    throw new Error(await parseError(res, 'Não foi possível concluir o primeiro acesso.'));
  const body = (await res.json()) as { data?: unknown };
  return body.data;
}

export async function fetchFirstHouseStatus(session: StoredSession): Promise<unknown> {
  const res = await fetch('/api/v1/session/first-house', {
    headers: { Authorization: `Bearer ${session.accessToken}` },
  });
  if (!res.ok)
    throw new Error(
      await parseError(res, 'Não foi possível consultar o estado do primeiro acesso.'),
    );
  const body = (await res.json()) as { data?: unknown };
  return body.data ?? null;
}

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(current: StoredSession): Promise<boolean> {
  const res = await fetch('/api/v1/session/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) return false;
  const body = (await res.json()) as LoginResponse;
  saveSession({ ...current, ...body.data });
  return true;
}

/**
 * Renova a sessão com o refresh token. Chamadas concorrentes compartilham a
 * mesma renovação (rotação do Phoenix invalida o token anterior — dois
 * refreshes paralelos derrubariam a sessão).
 */
export async function refreshSession(): Promise<boolean> {
  const current = getSession();
  if (!current) return false;
  refreshInFlight ??= doRefresh(current)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export function logout(): void {
  clearSession();
  window.location.assign('/login');
}

/** Sessão caiu (401 com token real): limpa e volta para o login. */
export function handleUnauthorized(): void {
  clearSession();
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}
